import { Alert, Button, Form, Select, Space, Typography } from 'antd'
import {
  containsRectangle,
  isValidRectangle,
} from 'features/documentMarkup/editor/markupGeometry'
import { useLibraDocument } from 'features/libra/document/LibraDocumentContext'
import { DocumentDrawingControls } from 'features/libra/drawing/DocumentDrawingControls'
import { DocumentDrawingView } from 'features/libra/drawing/DocumentDrawingView'
import type { MeasuredImage } from 'features/libra/drawing/pixelRectangleValidation'
import { pixelRectangleFromBounds } from 'features/libra/drawing/pixelRectangleValidation'
import { useDocumentDrawing } from 'features/libra/drawing/useDocumentDrawing'
import { InspectionAreaCard } from 'features/libra/inspectionPlanning/InspectionAreaCard'
import { InspectionZoneSummary } from 'features/libra/inspectionPlanning/InspectionZoneSummary'
import { PlanningWorkPackExport } from 'features/libra/inspectionPlanning/pdf/PlanningWorkPackExport'
import {
  analyzeInspectionAreaGeometry,
  areInspectionAreasComplete,
  buildInspectionAreaPixelPolicy,
  canRedrawInspectionArea,
  findInspectionAreaSourceBoxIndex,
  getInspectionAreaGeometryError,
  inspectionAreaGeometryLabels,
  readInspectionAreas,
} from 'features/libra/inspectionPlanning/planningModel'
import {
  getPlanningStrategy,
  getRequiredInspectionArea,
} from 'features/libra/inspectionPlanning/planningStrategy'
import {
  InspectionArea,
  InspectionAreaDrawing,
  InspectionAreaUpdate,
} from 'features/libra/inspectionPlanning/planningTypes'
import { useInspectionAreasDraft } from 'features/libra/inspectionPlanning/useInspectionAreasDraft'
import { wrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { useActiveScopeOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { zoneBoxLabel } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { useReportValidation } from 'features/libra/StepValidationContext'
import { useTicketAccess } from 'features/libra/ticket/TicketAccessContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'

import { InspectionAreaSourceBoxChooser } from './InspectionAreaSourceBoxChooser'
import { parseInspectionRecommendations } from './niiReport/inspectionRecommendations'
import { NiiSaveBanner } from './niiReport/NiiSaveBanner'
import { CropDocumentPanel } from './zoneAnalysis/components/CropDocumentPanel'

type PlanningAreas = Extract<
  ReturnType<typeof readInspectionAreas>,
  { success: true }
>['data']

type InspectionSelection =
  | { kind: 'source'; id: string }
  | { kind: 'area'; id: string }

type DrawingRegionSelection =
  | { kind: 'source'; id: string; boxIndex: number }
  | { kind: 'area'; id: string }

type SourceBoxChoice = Readonly<{
  sourceZoneId: string
  sourceRevision: string
  sourceKey: string
  boxIndex: number
}>

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const sourceOverlayId = (id: string, boxIndex: number) =>
  `nii-source-${id}-${boxIndex}`
const areaOverlayId = (id: string) => `nii-area-${id}`

export const InspectionAreas = ({
  planning,
  sourceCaptureIdentity,
  readonly,
}: {
  planning: PlanningAreas
  sourceCaptureIdentity: string
  readonly?: boolean
}) => {
  const { config, zones, areas: savedAreas, strategies, catalogue } = planning
  const { fieldValues, setFieldValue, readOnly, isLoading } = useTicketAccess()
  const disabled = Boolean(readonly || readOnly || isLoading)
  const { ontologyFields } = useActiveScopeOntologyFields()
  const areaDefinition = ontologyFields[config.areasField]
  const areaProperties =
    areaDefinition?.category === 'input'
      ? areaDefinition.items?.properties
      : undefined
  const persist = useCallback(
    (areas: InspectionArea[]) =>
      setFieldValue(config.areasField, wrapFieldValue(areas)),
    [setFieldValue, config.areasField]
  )
  const [selection, setSelection] = useState<InspectionSelection>()
  const [sourceBoxChoice, setSourceBoxChoice] = useState<SourceBoxChoice>()
  const [drawingResetKey, setDrawingResetKey] = useState(0)
  const [measuredImage, setMeasuredImage] = useState<MeasuredImage>()
  const areaCardRefs = useRef(new Map<string, HTMLDivElement>())
  const zoneSummaryRef = useRef<HTMLDivElement>(null)
  const geometryContext = useMemo(
    () => ({ zones, measuredImage }),
    [zones, measuredImage]
  )
  const draft = useInspectionAreasDraft(
    savedAreas,
    persist,
    disabled,
    geometryContext
  )
  const { areas, hasChanges, saving, error, save, add, update, remove } = draft
  const geometryProjection = useMemo(
    () => ({
      zones: zones.map((zone) =>
        pick(zone, ['id', 'zone_reference_image_id', 'drawing_bounds'])
      ),
      areas: areas.map((area) =>
        pick(area, [
          'id',
          'sourceZoneId',
          'scope',
          'zone_reference_image_id',
          'drawing_bounds',
        ])
      ),
      measuredImage:
        measuredImage && pick(measuredImage, ['documentId', 'width', 'height']),
    }),
    [areas, measuredImage, zones]
  )
  const geometryCache = useRef<{
    projection: typeof geometryProjection
    analysis: ReturnType<typeof analyzeInspectionAreaGeometry>
  }>()
  const geometryAnalysis = useMemo(() => {
    if (
      geometryCache.current &&
      isEqual(geometryCache.current.projection, geometryProjection)
    )
      return geometryCache.current.analysis
    const analysis = analyzeInspectionAreaGeometry({
      areas,
      zones,
      measuredImage,
    })
    geometryCache.current = { projection: geometryProjection, analysis }
    return analysis
  }, [areas, geometryProjection, measuredImage, zones])
  const geometryLabels = useMemo(
    () => inspectionAreaGeometryLabels(areas, zones),
    [areas, zones]
  )
  const selectedArea =
    selection?.kind === 'area'
      ? areas.find((area) => area.id === selection.id)
      : undefined
  const zoneId =
    selectedArea?.sourceZoneId ??
    (selection?.kind === 'source' ? selection.id : undefined)
  const zone =
    zoneId !== undefined ? zones.find((item) => item.id === zoneId) : zones[0]
  const strategyConfig = zone
    ? getPlanningStrategy(zone, strategies)
    : undefined
  const requiredArea = zone
    ? getRequiredInspectionArea(zone, strategies)
    : undefined
  const recommendations = parseInspectionRecommendations({
    inspection_recommendations: fieldValues[config.recommendationsField],
  })
  const zoneRecommendation = recommendations.perZone.find(
    (item) => item.zone_id === zone?.id
  )?.recommendation_text
  const zoneAreas = zone
    ? areas.filter((area) => area.sourceZoneId === zone.id)
    : selectedArea
      ? [selectedArea]
      : []
  const areaGeometryError = selectedArea
    ? getInspectionAreaGeometryError({
        geometry: geometryAnalysis,
        areaId: selectedArea.id,
        labels: geometryLabels,
      })
    : undefined
  const sourceError =
    selectedArea && !zone
      ? `${selectedArea.title} cannot be edited because its source zone is unavailable.`
      : selection?.kind === 'source' && !zone
        ? 'The selected source zone is unavailable. New areas and redraw are unavailable.'
        : undefined
  const sourceImageId = zone?.zone_reference_image_id
  const sourceBoxes = useMemo(
    () =>
      zone ? (geometryAnalysis.sourcesById.get(zone.id)?.boxes ?? []) : [],
    [geometryAnalysis, zone]
  )
  const sourceErrorMessage = sourceError
  const { unitId, selectedDocumentId, drawingHost, drawingTarget } =
    useLibraDocument()
  const sourceGeometryRevision = JSON.stringify(
    zones.map((item) => [
      item.id,
      item.zone_reference_image_id,
      sourceCaptureIdentity,
      (item.drawing_bounds ?? []).map(({ x_start, y_start, x_end, y_end }) => [
        x_start,
        y_start,
        x_end,
        y_end,
      ]),
    ])
  )
  const boundDrawingSourceKey =
    selectedDocumentId &&
    drawingHost?.documentId === selectedDocumentId &&
    drawingTarget?.documentId === selectedDocumentId &&
    drawingTarget?.url &&
    measuredImage?.documentId === selectedDocumentId
      ? JSON.stringify([drawingTarget.documentId, drawingTarget.url])
      : undefined
  const drawingSourceKey =
    selectedDocumentId === sourceImageId ? boundDrawingSourceKey : undefined
  const isCurrentSourceBoxChoice = useCallback(
    (choice?: SourceBoxChoice) =>
      Boolean(
        choice &&
        zone &&
        sourceImageId &&
        drawingSourceKey &&
        choice.sourceZoneId === zone.id &&
        choice.sourceRevision === sourceGeometryRevision &&
        choice.sourceKey === drawingSourceKey &&
        sourceBoxes.some((box) => box.index === choice.boxIndex)
      ),
    [drawingSourceKey, sourceBoxes, sourceGeometryRevision, sourceImageId, zone]
  )
  const sourceBoxChoiceIsCurrent = isCurrentSourceBoxChoice(sourceBoxChoice)
  const selectedSourceBoxIndex = sourceBoxChoiceIsCurrent
    ? sourceBoxChoice?.boxIndex
    : undefined
  const singleSourceBoxIndex =
    drawingSourceKey && sourceBoxes.length === 1
      ? sourceBoxes[0]?.index
      : undefined
  const highlightedSourceBoxIndex =
    selectedSourceBoxIndex ?? singleSourceBoxIndex
  const currentSourceBoxChoice = useMemo(
    () =>
      sourceBoxChoiceIsCurrent
        ? sourceBoxChoice
        : zone &&
            sourceImageId &&
            drawingSourceKey &&
            singleSourceBoxIndex !== undefined
          ? {
              sourceZoneId: zone.id,
              sourceRevision: sourceGeometryRevision,
              sourceKey: drawingSourceKey,
              boxIndex: singleSourceBoxIndex,
            }
          : undefined,
    [
      drawingSourceKey,
      singleSourceBoxIndex,
      sourceBoxChoice,
      sourceBoxChoiceIsCurrent,
      sourceGeometryRevision,
      sourceImageId,
      zone,
    ]
  )

  const saveGuard = useRegisterSaveGuard({
    hasUnsavedEdits: !disabled && hasChanges,
    save,
  })
  const complete = areInspectionAreasComplete(
    zones,
    areas,
    config,
    strategies,
    catalogue
  )
  useReportValidation(
    disabled || complete
      ? undefined
      : {
          status: 'Error',
          message:
            'Each zone needs its source strategy and coverage requirement. Complete the required locations, techniques, instructions and planned quantities.',
        }
  )

  const drawingData = useMemo(() => {
    const records = new Map<string, DrawingRegionSelection>()
    const sourceImageIdByZoneId = new Map(
      zones.map((item) => [item.id, item.zone_reference_image_id])
    )
    const regions = [
      ...zones.flatMap((item) =>
        (item.drawing_bounds ?? []).map((bounds, boxIndex) => {
          const id = sourceOverlayId(item.id, boxIndex)
          records.set(id, { kind: 'source', id: item.id, boxIndex })
          return {
            id,
            title: zoneBoxLabel(item.title, boxIndex),
            color: '#64748B',
            readOnly: true,
            ...(item.zone_reference_image_id
              ? { zone_reference_image_id: item.zone_reference_image_id }
              : {}),
            drawing_bounds: {
              x_start: bounds.x_start,
              y_start: bounds.y_start,
              x_end: bounds.x_end,
              y_end: bounds.y_end,
            },
          }
        })
      ),
      ...areas
        .filter((area) => area.scope !== 'zone')
        .map((area) => {
          const id = areaOverlayId(area.id)
          records.set(id, { kind: 'area', id: area.id })
          return {
            id,
            title: area.title,
            color: '#0D9488',
            ...(area.zone_reference_image_id
              ? { zone_reference_image_id: area.zone_reference_image_id }
              : {}),
            ...(area.drawing_bounds
              ? { drawing_bounds: area.drawing_bounds }
              : {}),
            redrawTargetDocumentId: sourceImageIdByZoneId.get(
              area.sourceZoneId
            ),
          }
        }),
    ]
    return { records, sourceImageIdByZoneId, regions }
  }, [areas, zones])
  const selectedRegionId = selectedArea
    ? selectedArea.scope === 'zone'
      ? highlightedSourceBoxIndex === undefined
        ? undefined
        : sourceOverlayId(selectedArea.sourceZoneId, highlightedSourceBoxIndex)
      : areaOverlayId(selectedArea.id)
    : zone && highlightedSourceBoxIndex !== undefined
      ? sourceOverlayId(zone.id, highlightedSourceBoxIndex)
      : undefined
  const measurementError =
    zone && sourceImageId && measuredImage?.documentId !== sourceImageId
      ? `Open ${zone.title}'s reference image before adding or changing a rectangle.`
      : undefined
  const chooseSourceBoxRef = useRef<
    (sourceZoneId: string, boxIndex: number) => void
  >(() => {})
  const updateDrawing = useCallback(
    (
      areaDrawing: InspectionAreaDrawing,
      overlayId?: string,
      capturedChoice?: SourceBoxChoice
    ) => {
      const choice = capturedChoice ?? currentSourceBoxChoice
      const selectedSourceBox = choice
        ? geometryAnalysis.sourcesById
            .get(choice.sourceZoneId)
            ?.boxes.find((box) => box.index === choice.boxIndex)
        : undefined
      const drawnRectangle = pixelRectangleFromBounds(
        areaDrawing.drawing_bounds
      )
      if (
        !zone ||
        !sourceImageId ||
        !choice ||
        !isCurrentSourceBoxChoice(choice) ||
        choice.boxIndex !== highlightedSourceBoxIndex ||
        areaDrawing.zone_reference_image_id !== sourceImageId ||
        !selectedSourceBox ||
        !isValidRectangle(drawnRectangle) ||
        !containsRectangle(selectedSourceBox.boundary, drawnRectangle)
      )
        return undefined
      const selectedRegion = overlayId
        ? drawingData.records.get(overlayId)
        : undefined
      if (overlayId && selectedRegion?.kind !== 'area') return undefined
      const areaId =
        selectedRegion?.kind === 'area' ? selectedRegion.id : undefined
      const acceptedId =
        areaId !== undefined
          ? update(areaId, areaDrawing)
          : add(zone, areaDrawing)
      if (acceptedId === undefined) return undefined
      setSelection(() => ({ kind: 'area', id: acceptedId }))
      setSourceBoxChoice(undefined)
      return areaOverlayId(acceptedId)
    },
    [
      add,
      currentSourceBoxChoice,
      drawingData,
      geometryAnalysis,
      highlightedSourceBoxIndex,
      isCurrentSourceBoxChoice,
      sourceImageId,
      update,
      zone,
    ]
  )
  const updateSelectedArea = useCallback(
    (regionId: string, bounds: InspectionArea['drawing_bounds']) => {
      const selected = drawingData.records.get(regionId)
      if (selected?.kind === 'area' && bounds)
        update(selected.id, { drawing_bounds: bounds })
    },
    [drawingData, update]
  )
  const selectRegion = useCallback(
    (regionId: string | undefined) => {
      if (!regionId) {
        setSelection(undefined)
        return
      }
      const target = drawingData.records.get(regionId)
      if (target?.kind === 'source')
        chooseSourceBoxRef.current(target.id, target.boxIndex)
      else if (target) setSelection(target)
    },
    [drawingData]
  )
  const drawing = useDocumentDrawing<SourceBoxChoice>({
    regions: drawingData.regions,
    unitId,
    disabled: disabled || saving || !zone,
    isLoading,
    onDraw: updateDrawing,
    onImageNaturalSize: setMeasuredImage,
    selectedRegionId,
    onSelectRegion: selectRegion,
    onUpdateRegion:
      selectedArea && !areaGeometryError ? updateSelectedArea : undefined,
    interactionRevision: sourceGeometryRevision,
    drawingResetKey,
  })

  const replacementSelection = drawing.command?.replacementRegionId
    ? drawingData.records.get(drawing.command.replacementRegionId)
    : undefined
  const excludeAreaId = drawing.command
    ? replacementSelection?.kind === 'area'
      ? replacementSelection.id
      : undefined
    : selectedArea?.scope === 'zone'
      ? undefined
      : selectedArea?.id
  const inferredAreaSourceBoxIndex =
    selectedArea && selectedArea.scope !== 'zone'
      ? findInspectionAreaSourceBoxIndex(
          geometryAnalysis,
          selectedArea.sourceZoneId,
          selectedArea.drawing_bounds
        )
      : undefined
  const sourceBoxIndexForPolicy = drawing.command
    ? drawing.commandContext
      ? isCurrentSourceBoxChoice(drawing.commandContext) &&
        drawing.commandContext.boxIndex === highlightedSourceBoxIndex
        ? drawing.commandContext.boxIndex
        : undefined
      : currentSourceBoxChoice?.boxIndex
    : (inferredAreaSourceBoxIndex ?? currentSourceBoxChoice?.boxIndex)
  const canChooseSourceBox = Boolean(drawingSourceKey)
  const hasRequiredSourceBoxChoice =
    sourceBoxes.length <= 1 || sourceBoxChoiceIsCurrent
  const chooseSourceBox = (sourceZoneId: string, boxIndex: number) => {
    const sourceZone = zones.find((item) => item.id === sourceZoneId)
    const sourceBoxesForZone =
      geometryAnalysis.sourcesById.get(sourceZoneId)?.boxes ?? []
    if (
      !sourceZone?.zone_reference_image_id ||
      sourceZone.zone_reference_image_id !== selectedDocumentId ||
      !boundDrawingSourceKey ||
      !sourceBoxesForZone.some((box) => box.index === boxIndex)
    )
      return
    const previousChoice =
      isCurrentSourceBoxChoice(sourceBoxChoice) && sourceBoxChoice
        ? sourceBoxChoice
        : currentSourceBoxChoice
    const sameChoice =
      previousChoice?.sourceZoneId === sourceZoneId &&
      previousChoice.boxIndex === boxIndex
    if (!sameChoice) drawing.cancelDrawing()
    setSourceBoxChoice({
      sourceZoneId,
      sourceRevision: sourceGeometryRevision,
      sourceKey: boundDrawingSourceKey,
      boxIndex,
    })
    if (
      selectedArea?.scope === 'zone' ||
      selectedArea?.sourceZoneId !== sourceZoneId
    )
      setSelection({ kind: 'source', id: sourceZoneId })
  }
  chooseSourceBoxRef.current = chooseSourceBox
  const chooseSelectedZoneSourceBox = (boxIndex: number) => {
    if (zone) chooseSourceBox(zone.id, boxIndex)
  }
  const drawingPixelPolicy = useMemo(
    () =>
      zone
        ? buildInspectionAreaPixelPolicy({
            sourceZoneId: zone.id,
            geometry: geometryAnalysis,
            labels: geometryLabels,
            excludeAreaId,
            sourceBoxIndex: sourceBoxIndexForPolicy,
          })
        : undefined,
    [
      excludeAreaId,
      geometryAnalysis,
      geometryLabels,
      sourceBoxIndexForPolicy,
      zone,
    ]
  )
  const malformedGeometryError =
    drawingPixelPolicy && 'error' in drawingPixelPolicy
      ? drawingPixelPolicy.error
      : undefined
  const sourcePolicyIsCurrent =
    drawing.viewProps.target?.documentId === sourceImageId
  const { command: drawingCommand, cancelDrawing } = drawing

  useEffect(() => {
    if (
      drawingCommand &&
      sourcePolicyIsCurrent &&
      drawingPixelPolicy &&
      'error' in drawingPixelPolicy
    )
      cancelDrawing(drawingCommand.token)
  }, [cancelDrawing, drawingCommand, drawingPixelPolicy, sourcePolicyIsCurrent])

  const redrawableAreaIds = useMemo(
    () =>
      new Set(
        areas.flatMap((area) =>
          zone &&
          area.sourceZoneId === zone.id &&
          area.scope !== 'zone' &&
          !sourceErrorMessage &&
          canRedrawInspectionArea(geometryAnalysis, zone.id, area.id)
            ? [area.id]
            : []
        )
      ),
    [areas, geometryAnalysis, sourceErrorMessage, zone]
  )

  useEffect(() => {
    const id = selection?.kind === 'area' ? selection.id : undefined
    if (id !== undefined)
      areaCardRefs.current.get(id)?.scrollIntoView({ block: 'nearest' })
    else if (selection?.kind === 'source')
      zoneSummaryRef.current?.scrollIntoView({ block: 'nearest' })
  }, [selection?.kind, selection?.id])

  const selectZone = (id: string) => {
    setSelection(() => ({ kind: 'source', id }))
    setSourceBoxChoice(undefined)
    drawing.cancelDrawing()
    const sourceImage = drawingData.sourceImageIdByZoneId.get(id)
    if (sourceImage) drawing.openDocument(sourceImage)
  }
  const addWholeZone = () => {
    if (zone) {
      const id = draft.add(zone)
      if (id !== undefined) {
        setSelection(() => ({ kind: 'area', id }))
        setSourceBoxChoice(undefined)
        drawing.cancelDrawing()
      }
    }
  }
  const resetDraft = () => {
    draft.reset()
    setDrawingResetKey((key) => key + 1)
    setSelection(undefined)
    setSourceBoxChoice(undefined)
    drawing.cancelDrawing()
  }
  const saveDraft = async () => {
    try {
      await save()
    } catch {
      // The banner shows the error; the save guard still needs save() to reject.
    }
  }
  const deleteArea = (id: string) => {
    remove(id)
    setSelection((current) =>
      current?.kind === 'area' && current.id === id ? undefined : current
    )
    setSourceBoxChoice(undefined)
    drawing.cancelDrawing()
  }
  const showArea = (area: InspectionArea) => {
    setSourceBoxChoice(undefined)
    const regionId =
      area.scope === 'zone' || !area.zone_reference_image_id
        ? sourceOverlayId(area.sourceZoneId, 0)
        : areaOverlayId(area.id)
    const region = drawingData.regions.find((item) => item.id === regionId)
    if (region && area.scope !== 'zone') drawing.showRegion(region)
    else if (area.zone_reference_image_id)
      drawing.openDocument(area.zone_reference_image_id)
    else {
      const sourceImage = drawingData.sourceImageIdByZoneId.get(
        area.sourceZoneId
      )
      if (sourceImage) drawing.openDocument(sourceImage)
    }
    setSelection(() => ({ kind: 'area', id: area.id }))
  }
  const selectAreaCard = (area: InspectionArea) => {
    if (selection?.kind === 'area' && selection.id === area.id) return
    drawing.cancelDrawing()
    showArea(area)
  }
  const redrawArea = (area: InspectionArea) => {
    if (sourceBoxes.length > 1 && !sourceBoxChoiceIsCurrent) return
    const region = drawingData.regions.find(
      (item) => item.id === areaOverlayId(area.id)
    )
    if (area.scope !== 'zone' && region && redrawableAreaIds.has(area.id))
      drawing.showRegion(region, true)
  }
  const startPlanningDrawing = () => {
    if (!sourceImageId || !currentSourceBoxChoice || !selectedRegionId) return
    drawing.startDrawingForDocument(
      sourceImageId,
      currentSourceBoxChoice,
      selectedRegionId
    )
  }
  const openSourceDrawing = () => {
    if (sourceImageId) drawing.openDocument(sourceImageId)
  }
  const drawingControls = {
    ...drawing,
    canDraw:
      drawing.canDraw &&
      (drawing.isDrawing ||
        (measuredImage?.documentId === sourceImageId &&
          !sourceErrorMessage &&
          !malformedGeometryError &&
          hasRequiredSourceBoxChoice)),
    startDrawing: startPlanningDrawing,
  }
  const renderArea = (area: InspectionArea) => {
    const setAreaCardRef = (node: HTMLDivElement | null) => {
      if (node) areaCardRefs.current.set(area.id, node)
      else areaCardRefs.current.delete(area.id)
    }
    const handleSelect = () => selectAreaCard(area)
    const handleChange = (update: InspectionAreaUpdate) =>
      draft.update(area.id, update)
    const handleShow = () => showArea(area)
    const handleRedraw = () => redrawArea(area)
    const handleDelete = () => deleteArea(area.id)
    const canShow = Boolean(
      area.scope === 'zone' || !area.zone_reference_image_id
        ? drawingData.sourceImageIdByZoneId.get(area.sourceZoneId)
        : area.zone_reference_image_id
    )

    return (
      <div key={area.id} ref={setAreaCardRef}>
        <InspectionAreaCard
          area={area}
          titleLabel={areaProperties?.title?.name ?? 'Area label'}
          config={config}
          catalogue={catalogue}
          sourceRequiredArea={requiredArea}
          readonly={disabled}
          saving={saving}
          selected={selection?.kind === 'area' && selection.id === area.id}
          geometryError={
            selectedArea?.id === area.id
              ? (areaGeometryError ?? sourceErrorMessage)
              : undefined
          }
          canRedraw={
            !disabled &&
            !saving &&
            (sourceBoxes.length <= 1 || sourceBoxChoiceIsCurrent) &&
            redrawableAreaIds.has(area.id) &&
            area.scope !== 'zone'
          }
          canShow={canShow}
          onSelect={handleSelect}
          onChange={handleChange}
          onShow={handleShow}
          onRedraw={handleRedraw}
          onDelete={handleDelete}
        />
      </div>
    )
  }

  return (
    <Container>
      <DocumentDrawingView
        {...drawing.viewProps}
        pixelPolicy={sourcePolicyIsCurrent ? drawingPixelPolicy : undefined}
      />
      <SaveGuardModal {...saveGuard} />
      {!disabled && (
        <NiiSaveBanner
          hasChanges={hasChanges}
          isLoading={saving}
          onReset={resetDraft}
          onSave={saveDraft}
        />
      )}
      {error && <Alert type='error' title={error} />}
      {config.workPack && zones.length > 0 && (
        <PlanningWorkPackExport
          config={config}
          exportConfig={config.workPack}
          areas={areas}
        />
      )}
      <Form.Item label='NII zone'>
        <Select
          value={zone?.id}
          onChange={selectZone}
          options={zones.map((item) => ({ value: item.id, label: item.title }))}
        />
      </Form.Item>
      {zone || selectedArea ? (
        <>
          {zone && (
            <>
              {strategyConfig && (
                <div ref={zoneSummaryRef}>
                  <InspectionZoneSummary
                    zone={zone}
                    strategyConfig={strategyConfig}
                    requiredArea={requiredArea}
                    recommendation={zoneRecommendation}
                    areas={zoneAreas}
                    config={config}
                    catalogue={catalogue}
                  />
                </div>
              )}
              {sourceBoxes.length > 1 && (
                <InspectionAreaSourceBoxChooser
                  value={selectedSourceBoxIndex}
                  disabled={disabled || saving || !canChooseSourceBox}
                  options={sourceBoxes.map(({ index }) => ({
                    value: index,
                    label: zoneBoxLabel(zone.title, index),
                  }))}
                  onChange={chooseSelectedZoneSourceBox}
                />
              )}
              <Space wrap>
                {!disabled &&
                  strategyConfig &&
                  !strategyConfig.detailedDrawing && (
                    <Button disabled={saving} onClick={addWholeZone}>
                      Add whole-zone scope
                    </Button>
                  )}
                {zone.zone_reference_image_id && (
                  <Button onClick={openSourceDrawing}>Open NII drawing</Button>
                )}
                {!disabled && (
                  <DocumentDrawingControls
                    drawing={drawingControls}
                    drawLabel={
                      strategyConfig?.detailedDrawing
                        ? 'Draw inspection area'
                        : 'Add optional area'
                    }
                    cropLabel='Use selected GA/PDF as drawing'
                  />
                )}
              </Space>
              {drawing.isDrawing && (
                <Alert
                  type='info'
                  title='Drag a box on the drawing in the document panel'
                />
              )}
            </>
          )}
          {(sourceErrorMessage ||
            malformedGeometryError ||
            (!selectedArea && measurementError)) && (
            <Alert
              type='warning'
              title={
                sourceErrorMessage || malformedGeometryError || measurementError
              }
            />
          )}
          {drawing.crop && <CropDocumentPanel {...drawing.crop} />}
          {zoneAreas.map(renderArea)}
          {zone && (
            <Typography.Text type='secondary'>
              Boxes locate inspection areas on the drawing. Physical scan area
              is entered separately; drawing pixels do not measure inspection
              coverage.
            </Typography.Text>
          )}
        </>
      ) : (
        <Alert
          type='warning'
          title='No NII assessment basis is available'
          description='Save a completed NII assessment with confirmed zones in the preparation step before planning inspection areas.'
        />
      )}
    </Container>
  )
}
